// Appointments tab — calendar, booking, scheduled-day generation.
import { useState, useEffect, useMemo } from "react";
import { doc, setDoc, deleteDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { syncCol } from "../lib/sync";
import { clientToday, canMarkAttendance, isSessionBased, toMins, packageExpiryOf } from "../domain";
import { uid, cl } from "../lib/format";
import { markCounterPatch } from "../lib/packages";
import { themeTokens } from "../lib/theme";
import { Modal, Btn, Inp, Sel, SD, Empty, Icon, ConfirmButton, useToast, useFormGuard } from "../ui/primitives";

export const EA={clientName:"",date:clientToday(),time:"08:00",duration:60,type:"Session",notes:"",status:"Upcoming"};

export const getScheduledDays=(clients,yr,mo)=>{
  const dayMap={"Sunday":0,"Monday":1,"Tuesday":2,"Wednesday":3,"Thursday":4,"Friday":5,"Saturday":6};
  const dim=new Date(yr,mo+1,0).getDate();
  const todayStr=clientToday();
  const result={};

  clients.forEach(c=>{
    if(!c.schedule?.length)return;
    const pkg=c.package;
    const hasPackage=pkg?.type&&pkg.type!=="none"&&pkg.startDate;
    const startDate=pkg?.startDate||null;
    const expiry=hasPackage?packageExpiryOf(c):null;
    const sessionBased=isSessionBased(c);
    const remaining=c.classesRemaining||0;

    // For session-based: generate exactly `remaining` future slots across all time
    // For time-based: generate slots up to expiry date
    // No package: generate indefinitely

    // Count future slots already generated for session-based
    let slotsGenerated=0;

    // Collect all future slot dates first for session-based (need to sort chronologically)
    if(sessionBased&&hasPackage){
      if(remaining<=0)return; // No slots if package exhausted
      // Generate future slots across a wider window (up to 1 year)
      const allFutureSlots=[];
      for(let month=0;month<12;month++){
        const scanYr=month===0?yr:(mo+month>11?yr+1:yr);
        const scanMo=(mo+month)%12;
        const scanDim=new Date(scanYr,scanMo+1,0).getDate();
        for(let d=1;d<=scanDim;d++){
          const ds=`${scanYr}-${String(scanMo+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
          if(ds<todayStr)continue;
          if(startDate&&ds<startDate)continue;
          const date=new Date(scanYr,scanMo,d);
          c.schedule.forEach(rule=>{
            (rule.days||[]).forEach(day=>{
              if(date.getDay()===dayMap[day]){
                allFutureSlots.push({ds,time:rule.time,duration:rule.duration||60});
              }
            });
          });
        }
      }
      // Sort and take only `remaining` slots
      allFutureSlots.sort((a,b)=>a.ds.localeCompare(b.ds)||a.time.localeCompare(b.time));
      const slotsTaken=new Set();
      let count=0;
      allFutureSlots.forEach(slot=>{
        const key=`${slot.ds}_${slot.time}`;
        if(slotsTaken.has(key))return;
        slotsTaken.add(key);
        if(count>=remaining)return;
        // Only add if in current month view
        const [sy,sm]=slot.ds.split("-").map(Number);
        if(sy===yr&&sm===mo+1){
          if(!result[slot.ds])result[slot.ds]=[];
          result[slot.ds].push({clientName:c.name,clientId:c.id,time:slot.time,duration:slot.duration,type:"Class",remaining:remaining-count});
        }
        count++;
      });
      return;
    }

    // Time-based or no package
    for(let d=1;d<=dim;d++){
      const date=new Date(yr,mo,d);
      const ds=`${yr}-${String(mo+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
      if(ds<todayStr)continue;
      if(startDate&&ds<startDate)continue;
      if(expiry&&ds>expiry)continue; // Stop at expiry
      c.schedule.forEach(rule=>{
        (rule.days||[]).forEach(day=>{
          if(date.getDay()===dayMap[day]){
            if(!result[ds])result[ds]=[];
            result[ds].push({clientName:c.name,clientId:c.id,time:rule.time,duration:rule.duration||60,type:"Class"});
          }
        });
      });
    }
  });
  return result;
};

export const Appointments=({clients,setClients,theme,userId,profile,appointments:rootApts=[],setAppointments:setRootApts})=>{
  const toast=useToast();
  // Seed from root state so there is no empty flash on tab switch
  const [apts,setApts]=useState(rootApts);
  useEffect(()=>{
    if(!userId)return;
    // Refresh from Firestore in background; existing rootApts already visible.
    // syncCol is the same offline-safe helper App.js uses for this collection —
    // a raw getDocs() here would skip its guard against an empty offline
    // snapshot clobbering already-good data. seedFirst:false — rootApts already
    // seeded `apts` above.
    syncCol(userId,"appointments",(fresh)=>{
      setApts(fresh);
      if(setRootApts)setRootApts(fresh);
    },false);
  },[userId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [modal,setModal]=useState(false);
  const [form,setForm]=useState(EA);
  const [conflictError,setConflictError]=useState("");
  useFormGuard(!!modal); // appointment form open — don't let sync clobber it
  const [editId,setEditId]=useState(null);
  const [calView,setCalView]=useState(true);
  const [calMonth,setCalMonth]=useState(new Date());
  const [showScheduled,setShowScheduled]=useState(true);
  const [selectedDay,setSelectedDay]=useState(null);
  const [completeModal,setCompleteModal]=useState(null);
  const [completeNote,setCompleteNote]=useState("");
  const {isDark,cb,bc,tc,mc}=themeTokens(theme);

  const save2=async(next)=>{
    setApts(prev=>{
      if(userId){
        next.forEach(apt=>{
          const{id,...data}=apt;
          setDoc(doc(db,"users",userId,"appointments",id),data).catch(e=>{
            console.error("Appointment save failed:",e);
            toast("Couldn't save appointment — check your connection and try again.","error");
          });
        });
        prev.forEach(apt=>{
          if(!next.find(x=>x.id===apt.id)){
            deleteDoc(doc(db,"users",userId,"appointments",apt.id)).catch(e=>console.error("Appointment delete failed:",e));
          }
        });
      }
      return next;
    });
    if(setRootApts)setRootApts(next);
  };
  const save=()=>{
    // Resolve clientName — if Other/Trial, use the manual entry
    const resolvedName=(form.clientName==="Other"||form._isOther)
      ?(form._otherName||"").trim()
      :form.clientName;
    if(!resolvedName){setConflictError("Please enter a name.");return;}
    if(!form.date){setConflictError("Please select a date.");return;}
    // Past date+time check
    const nowMs=new Date().getTime();
    const aptMs=new Date(`${form.date}T${form.time||"00:00"}`).getTime();
    if(aptMs<nowMs&&!form.id){setConflictError("Cannot schedule an appointment in the past.");return;}
    const client=clients.find(c=>c.name===form.clientName);
    const pkgStart=client?.package?.startDate;
    if(pkgStart&&form.date<pkgStart){
      setConflictError("This client's package starts on "+pkgStart+". Please pick a later date.");
      return;
    }
    // Duration-based conflict detection
    const newStart=toMins(form.time);
    if(newStart==null){setConflictError("Enter a valid time (e.g. 9:30 AM).");return;}
    const newEnd=newStart+(parseInt(form.duration)||60);
    const conflict=apts.find(a=>{
      if(a.id===form.id||a.date!==form.date||a.status==="Cancelled")return false;
      const aStart=toMins(a.time);
      if(aStart==null)return false;
      const aEnd=aStart+(parseInt(a.duration)||60);
      return newStart<aEnd&&newEnd>aStart;
    });
    if(conflict){
      setConflictError(`Time conflict with ${conflict.clientName} at ${conflict.time}. Please choose a time at least 1 hour apart.`);
      return;
    }
    setConflictError("");
    const {_otherName,_isOther,...restForm}=form;
    const finalForm={...restForm,clientName:resolvedName};
    if(form.id)save2(apts.map(a=>a.id===form.id?finalForm:a));
    else save2([{...finalForm,id:uid()},...apts]);
    setModal(false);setForm(EA);setEditId(null);
  };
  const upd=(id,status,note)=>save2(apts.map(a=>a.id===id?{...a,status,...(note?{completionNote:note}:{})}:a));
  const confirmComplete=()=>{
    if(!completeModal)return;
    upd(completeModal.id,"Completed",completeNote);
    setCompleteModal(null);
    setCompleteNote("");
  };
  const del=(id)=>save2(apts.filter(a=>a.id!==id));

  const [markedFromSchedule,setMarkedFromSchedule]=useState({}); // key: clientId_date_time → true

  const markAttendanceFromSchedule=(slot,date)=>{
    const client=clients.find(c=>c.id===slot.clientId);
    if(!client)return;
    // Appointments has no attendance-records prop, so dedup via markedFromSchedule.
    const key=`${client.id}_${date}_${slot.time}`;
    if(markedFromSchedule[key]){toast("Already marked for this slot.","warning");return;}
    const g=canMarkAttendance(client,date,{existingForClient:[],now:new Date()});
    if(!g.ok&&g.reason!=="duplicate"){
      toast(g.reason==="future"?"Cannot mark attendance for a future date.":`${client.name}'s package needs renewal.`,g.reason==="future"?"warning":"error");
      return;
    }
    const rec={id:uid(),clientId:client.id,clientName:client.name,date,time:slot.time,note:"Marked from schedule"};
    setMarkedFromSchedule(prev=>({...prev,[key]:true}));
    setClients(prev=>prev.map(c=>c.id===client.id?{...c,...markCounterPatch(c)}:c)); // App-level setClients persists the client doc
    toast(`Attendance marked for ${client.name} ✓`,"success");
    if(userId){
      setDoc(doc(db,"users",userId,"attendance",rec.id),{clientId:rec.clientId,clientName:rec.clientName,date:rec.date,time:rec.time,note:rec.note})
        .catch(e=>{console.error("Attendance save failed:",e);toast("Attendance didn't sync — it'll retry when you're online.","warning");});
    }
  };

  const yr=calMonth.getFullYear(),mo=calMonth.getMonth();
  const fd=new Date(yr,mo,1).getDay();
  const dim=new Date(yr,mo+1,0).getDate();
  const mn=calMonth.toLocaleDateString("en-US",{month:"long",year:"numeric"});
  const dayApts=(d)=>{const ds=`${yr}-${String(mo+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;return apts.filter(a=>a.date===ds);};
  const scheduledDays=useMemo(()=>showScheduled?getScheduledDays(clients,yr,mo):{},[showScheduled,clients,yr,mo]);
  const dayScheduled=(d)=>{const ds=`${yr}-${String(mo+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;return scheduledDays[ds]||[];};

  // All scheduled slots for list view
  const allScheduled=Object.entries(scheduledDays).sort(([a],[b])=>a.localeCompare(b));

  // Day view data
  const selDayApts=selectedDay?apts.filter(a=>a.date===selectedDay).sort((a,b)=>a.time.localeCompare(b.time)):[];
  const selDayScheduled=selectedDay?(scheduledDays[selectedDay]||[]):[];
  const selDayLabel=selectedDay?new Date(selectedDay+"T00:00").toLocaleDateString("en-US",{weekday:"long",day:"numeric",month:"long"}):"";

  return(
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold" style={{color:tc}}>Appointments</h2>
        <div className="flex gap-2">
          <button onClick={()=>setCalView(!calView)}
            className="px-3 py-2 rounded-xl text-xs font-semibold border transition-all"
            style={{background:calView?(theme?.primary||"#0f766e"):(isDark?"#1e293b":"#fff"),color:calView?"#fff":mc,borderColor:isDark?"#334155":"#e5e7eb"}}>
            {calView?"List":"Calendar"}
          </button>
          <Btn onClick={()=>{setForm({...EA,date:selectedDay||clientToday()});setModal(true);}} theme={theme}><Icon name="plus" size={16}/>Add</Btn>
        </div>
      </div>

      {/* Toggle recurring classes */}
      <div className="flex items-center gap-2">
        <button onClick={()=>setShowScheduled(s=>!s)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all"
          style={{background:showScheduled?(theme?.light||"#ccfbf1"):(isDark?"#1e293b":"#f9fafb"),color:showScheduled?(theme?.primary||"#0f766e"):mc,borderColor:showScheduled?(theme?.primary||"#0f766e"):(isDark?"#334155":"#e5e7eb")}}>
          <div className="w-2 h-2 rounded-full" style={{background:showScheduled?(theme?.primary||"#0f766e"):"#d1d5db"}}/>
          {showScheduled?"Showing":"Show"} recurring classes
        </button>
      </div>

      {calView&&(
        <>
          <div className="rounded-2xl p-4 shadow-sm border" style={{background:cb,borderColor:bc}}>
            <div className="flex items-center justify-between mb-4">
              <button onClick={()=>{setCalMonth(new Date(yr,mo-1,1));setSelectedDay(null);}} className="p-2 rounded-lg text-lg font-bold" style={{color:theme?.primary}}>‹</button>
              <p className="font-semibold text-sm" style={{color:tc}}>{mn}</p>
              <button onClick={()=>{setCalMonth(new Date(yr,mo+1,1));setSelectedDay(null);}} className="p-2 rounded-lg text-lg font-bold" style={{color:theme?.primary}}>›</button>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-2">
              {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d=><p key={d} className="text-center text-xs font-bold" style={{color:mc}}>{d}</p>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array(fd).fill(null).map((_,i)=><div key={`e${i}`}/>)}
              {Array.from({length:dim},(_,i)=>i+1).map(d=>{
                const dateStr=`${yr}-${String(mo+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                const da=dayApts(d);
                const ds=dayScheduled(d);
                const isT=clientToday()===dateStr;
                const isSel=selectedDay===dateStr;
                const hasAny=da.length>0||ds.length>0;
                return(
                  <div key={d} className="rounded-lg p-1 min-h-[2.5rem] cursor-pointer transition-all"
                    style={{
                      background:isSel?"#1d4ed8":isT?(theme?.primary||"#0f766e"):hasAny?(theme?.light||"#ccfbf1"):"transparent",
                      outline:isSel?"2px solid #1d4ed8":"none"
                    }}
                    onClick={()=>setSelectedDay(isSel?null:dateStr)}>
                    <p className="text-xs font-semibold text-center" style={{color:isSel||isT?"#fff":tc}}>{d}</p>
                    {da.slice(0,1).map(a=><p key={a.id} className="text-[9px] truncate rounded px-0.5 mt-0.5" style={{background:theme?.primary||"#0f766e",color:"#fff"}}>{a.clientName}</p>)}
                    {ds.length>0&&<p className="text-[9px] truncate rounded px-0.5 mt-0.5" style={{background:isDark?"#334155":"#e0e7ff",color:isDark?"#a5b4fc":"#4338ca"}}>📅 {ds.length}</p>}
                  </div>
                );
              })}
            </div>
            {/* Legend */}
            <div className="flex gap-4 mt-3 pt-3 border-t" style={{borderColor:bc}}>
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full" style={{background:theme?.primary}}/><span className="text-xs" style={{color:mc}}>Appointment</span></div>
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full" style={{background:isDark?"#334155":"#e0e7ff"}}/><span className="text-xs" style={{color:mc}}>Recurring class</span></div>
            </div>
          </div>

          {/* ── DAY VIEW PANEL ── */}
          {selectedDay&&(
            <div className="rounded-2xl border shadow-sm overflow-hidden" style={{background:cb,borderColor:theme?.primary||"#0f766e"}}>
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3" style={{background:theme?.primary||"#0f766e"}}>
                <div>
                  <p className="text-white font-black text-sm">{selDayLabel}</p>
                  <p className="text-white text-xs opacity-80">{selDayApts.length} appointment{selDayApts.length!==1?"s":""}{selDayScheduled.length>0?" · "+selDayScheduled.length+" class"+(selDayScheduled.length!==1?"es":""):""}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={()=>{setForm({...EA,date:selectedDay});setModal(true);}}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-bold bg-white transition-all"
                    style={{color:theme?.primary||"#0f766e"}}>
                    <Icon name="plus" size={13}/>Add
                  </button>
                  <button onClick={()=>setSelectedDay(null)} className="p-1.5 rounded-xl bg-white bg-opacity-20" style={{color:"#fff"}}>
                    <Icon name="x" size={14}/>
                  </button>
                </div>
              </div>

              <div className="p-4 space-y-3">
                {/* Appointments for this day */}
                {selDayApts.length===0&&selDayScheduled.length===0&&(
                  <p className="text-sm text-center py-3" style={{color:mc}}>No appointments. Tap Add to schedule one.</p>
                )}
                {selDayApts.map(a=>(
                  <div key={a.id} className="flex items-start gap-3 p-3 rounded-xl" style={{background:isDark?"#0f172a":"#f8fafc"}}>
                    <div className="flex-shrink-0 w-14 text-center">
                      <p className="text-xs font-black" style={{color:a.time?theme?.primary:mc}}>{a.time||"(not selected)"}</p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm" style={{color:tc}}>{a.clientName}</p>
                      <p className="text-xs" style={{color:mc}}>{a.type}</p>
                      {a.notes&&<p className="text-xs italic mt-0.5" style={{color:mc}}>{a.notes}</p>}
                      {a.completionNote&&<p className="text-xs mt-0.5 px-2 py-1 rounded-lg" style={{background:isDark?"#14532d":"#dcfce7",color:"#16a34a"}}>📝 {a.completionNote}</p>}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="text-xs px-2 py-1 rounded-full font-semibold"
                        style={{background:a.status==="Upcoming"?"#dbeafe":a.status==="Completed"?"#dcfce7":"#f3f4f6",color:a.status==="Upcoming"?"#1d4ed8":a.status==="Completed"?"#16a34a":"#6b7280"}}>
                        {a.status}
                      </span>
                      {a.status==="Upcoming"&&<>
                        <button onClick={()=>{setCompleteModal(a);setCompleteNote("");}} className="p-1 rounded-lg" style={{color:"#16a34a"}}><Icon name="check" size={13}/></button>
                        <button onClick={()=>{setEditId(a.id);setForm({...a});setModal(true);}} className="p-1 rounded-lg" style={{color:theme?.primary||"#0f766e"}}><Icon name="edit" size={13}/></button>
                      </>}
                      <ConfirmButton label={<Icon name="trash" size={13}/>} confirmLabel="Delete?" onConfirm={()=>del(a.id)} danger className="p-1 rounded-lg" style={{color:"#ef4444"}}/>
                    </div>
                  </div>
                ))}
                {/* Recurring class slots for this day */}
                {selDayScheduled.length>0&&(
                  <div className="pt-2">
                    <p className="text-xs font-semibold mb-2" style={{color:mc}}>Recurring Classes</p>
                    {selDayScheduled.map((s,i)=>{
                      const alreadyBooked=selDayApts.some(a=>a.clientName===s.clientName&&a.time===s.time);
                      const client=clients.find(c=>c.id===s.clientId);
                      const exhausted=isSessionBased(client||{})&&(client?.classesRemaining||0)<=0;
                      const isFuture=selectedDay>clientToday();
                      const alreadyMarked=markedFromSchedule[`${s.clientId}_${selectedDay}_${s.time}`];
                      return(
                        <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl mb-1.5" style={{background:isDark?"#1e1b4b":"#f5f3ff",border:`1px solid ${isDark?"#312e81":"#e0e7ff"}`}}>
                          <p className="text-xs font-black w-14 text-center flex-shrink-0" style={{color:"#6d28d9"}}>{s.time}</p>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold" style={{color:tc}}>{s.clientName}</p>
                            <p className="text-xs" style={{color:"#a78bfa"}}>{s.duration} min · Recurring</p>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {exhausted?(
                              <span className="text-xs px-2 py-1 rounded-full font-semibold" style={{background:"#fef2f2",color:"#ef4444"}}>Package done</span>
                            ):alreadyMarked?(
                              <span className="text-xs px-2 py-1 rounded-full font-semibold" style={{background:"#dcfce7",color:"#16a34a"}}>✓ Done</span>
                            ):isFuture?(
                              <span className="text-xs px-2 py-1 rounded-full font-semibold" style={{background:"#f3f4f6",color:"#9ca3af"}}>Upcoming</span>
                            ):(
                              <button onClick={()=>markAttendanceFromSchedule(s,selectedDay)}
                                className="text-xs px-2.5 py-1.5 rounded-xl font-bold flex items-center gap-1"
                                style={{background:"#dcfce7",color:"#16a34a"}}>
                                <Icon name="check" size={12}/>Attended
                              </button>
                            )}
                            {alreadyBooked
                              ?<span className="text-xs px-2 py-1 rounded-full font-semibold" style={{background:"#dbeafe",color:"#1d4ed8"}}>✓ Booked</span>
                              :<button
                                onClick={()=>{
                                  setForm({...EA,clientName:s.clientName,date:selectedDay,time:s.time,duration:s.duration||60,type:"Session"});
                                  setConflictError("");
                                  setModal(true);
                                }}
                                className="text-xs px-2.5 py-1.5 rounded-xl font-bold flex-shrink-0 flex items-center gap-1"
                                style={{background:theme?.primary||"#0f766e",color:"#fff"}}>
                                + Book
                              </button>
                            }
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {!calView&&(
        <>
          {apts.length===0&&allScheduled.length===0&&<Empty icon="appointments" message="No appointments." cta="Add" onCta={()=>{setForm(EA);setModal(true);}} theme={theme}/>}
          {/* Manual appointments */}
          {apts.map(a=>(
            <div key={a.id} className="rounded-2xl p-4 shadow-sm border" style={{background:cb,borderColor:bc}}>
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <p className="font-bold truncate" style={{color:tc}}>{a.clientName}</p>
                  <p className="text-xs mt-0.5" style={{color:mc}}>{a.date} at {a.time||"(not selected)"} · {a.type}</p>
                  {a.notes&&<p className="text-xs mt-1 whitespace-pre-line" style={{color:mc}}>{a.notes}</p>}
                </div>
                <span className="text-xs px-2 py-1 rounded-full font-semibold flex-shrink-0 ml-2"
                  style={{background:a.status==="Upcoming"?"#dbeafe":a.status==="Completed"?"#dcfce7":"#f3f4f6",color:a.status==="Upcoming"?"#1d4ed8":a.status==="Completed"?"#16a34a":"#6b7280"}}>
                  {a.status}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 mt-3">
                <div className="flex gap-2 flex-wrap min-w-0">
                  {a.status==="Upcoming"&&<>
                    <Btn variant="secondary" className="text-xs py-1.5 px-3" onClick={()=>upd(a.id,"Completed")} theme={theme}><Icon name="check" size={13}/>Done</Btn>
                    <Btn variant="secondary" className="text-xs py-1.5 px-3" onClick={()=>{setEditId(a.id);setForm({...a});setModal(true);}} theme={theme}><Icon name="edit" size={13}/>Edit</Btn>
                    <Btn variant="secondary" className="text-xs py-1.5 px-3" onClick={()=>upd(a.id,"Cancelled")} theme={theme}>Cancel</Btn>
                  </>}
                </div>
                <ConfirmButton label={<Icon name="trash" size={14}/>} confirmLabel="Delete?" onConfirm={()=>del(a.id)} danger className="p-1.5 rounded-lg flex-shrink-0" style={{color:"#ef4444"}}/>
              </div>
            </div>
          ))}
          {/* Recurring class schedule blocks */}
          {showScheduled&&allScheduled.slice(0,10).map(([date,slots])=>(
            <div key={date} className="rounded-2xl p-4 shadow-sm border" style={{background:isDark?"#1e293b":"#f5f3ff",borderColor:isDark?"#334155":"#e0e7ff"}}>
              <p className="text-xs font-bold mb-2" style={{color:isDark?"#a5b4fc":"#4338ca"}}>📅 {date} — Recurring Classes</p>
              {slots.map((s,i)=>(
                <p key={i} className="text-xs py-1" style={{color:tc}}>{s.clientName} · {s.time}</p>
              ))}
            </div>
          ))}
        </>
      )}


      {modal&&(
        <Modal title={editId?"Edit Appointment":"New Appointment"} onClose={()=>{setModal(false);setForm(EA);setEditId(null);}} theme={theme}>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              {clients.length>0
                ?<SD label={`${cl(profile)} *`} value={form.clientName} onChange={v=>setForm(p=>({...p,clientName:v,_isOther:v==="__other__"}))}
                    options={[...clients].sort((a,b)=>a.name.localeCompare(b.name)).map(c=>c.name).concat(["Other"])}
                    placeholder="Search client..." theme={theme}/>
                :<Inp label={`${cl(profile)} Name *`} value={form.clientName} onChange={e=>setForm(p=>({...p,clientName:e.target.value}))} theme={theme}/>
              }
              {(form.clientName==="Other"||form._isOther)&&(
                <div className="mt-3"><Inp label="Name *" value={form._otherName||""} onChange={e=>setForm(p=>({...p,_otherName:e.target.value}))} theme={theme} placeholder="e.g. Walk-in, Trial client"/></div>
              )}
            </div>
            <Inp label="Date" type="date" value={form.date} onChange={e=>{setForm(p=>({...p,date:e.target.value}));setConflictError("");}} theme={theme}/>
            <Inp label="Start Time" type="time" value={form.time} onChange={e=>setForm(p=>({...p,time:e.target.value}))} theme={theme}/>
            <Sel label="Duration" value={String(form.duration||60)} onChange={e=>setForm(p=>({...p,duration:parseInt(e.target.value)}))} theme={theme}>
              {[30,45,60,75,90,120].map(d=><option key={d} value={d}>{d} min</option>)}
            </Sel>
            <Sel label="Type" value={form.type} onChange={e=>setForm(p=>({...p,type:e.target.value}))} theme={theme}>
              {["Session","Consultation","Trial","Follow-up","Other"].map(t=><option key={t}>{t}</option>)}
            </Sel>
            <Sel label="Status" value={form.status} onChange={e=>setForm(p=>({...p,status:e.target.value}))} theme={theme}>
              {["Upcoming","Completed","Cancelled"].map(s=><option key={s}>{s}</option>)}
            </Sel>
            <div className="col-span-2 flex flex-col gap-1">
              <label className="text-xs font-semibold uppercase tracking-wider" style={{color:isDark?"#94a3b8":"#6b7280"}}>Notes</label>
              <textarea className="border rounded-xl px-4 py-2.5 text-sm focus:outline-none resize-none"
                style={{background:isDark?"#0f172a":"#f9fafb",borderColor:isDark?"#334155":"#e5e7eb",color:tc}}
                rows={2} value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/>
            </div>
          </div>
          {conflictError&&(
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl mt-2" style={{background:"#fef2f2",border:"1px solid #fecaca"}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="3"/></svg>
              <p className="text-xs text-red-600 font-medium">{conflictError}</p>
            </div>
          )}
          <div className="flex justify-end gap-3 mt-4">
            <Btn variant="secondary" onClick={()=>{setModal(false);setForm(EA);setConflictError("");}} theme={theme}>Cancel</Btn>
            <Btn onClick={save} theme={theme}><Icon name="check" size={16}/>Save</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};
